import React, { FC, useCallback, useEffect } from 'react';

import {
  getPharmacyEventsByDate,
  getPharmacyProfessionalPictures,
  pharmacyActions,
  PharmacyLegendVariant,
  resetStatus,
  successSelector,
} from '@pharmaplan/common';

import { idStore } from '@pharmaplan/common/helpers/ServiceHandler';

import { setFooterShow, setTitle } from '../../../../actions/drawerActions';
import DrawerOutOfDateTitle from '../../../common/DrawerOutOfDateTitle';
import useMultiShiftFilter from '../../../../hooks/useMultiShiftFilter';
import { stackedChipsColors } from '../../../../helpers/Constants';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import useSelfService from '../../../../hooks/useSelfService';
import StackedBox from '../../../common/StackedBox';
import strings from '../../../../localization';
import ServiceCard from '../../ServiceCard';

import SummaryContent from './SummaryContent';
import styles from './styles';

const { booked, requested } = PharmacyLegendVariant;

interface IWorkshiftSummaryList {
  date: string;
  type: PharmacyLegendVariant;
  preventInteraction?: boolean;
}

const WorkshiftSummaryList: FC<IWorkshiftSummaryList> = ({
  date,
  type,
  preventInteraction,
}) => {
  const dispatch = useAppDispatch();

  const { pharmacyEventsList: events } = useMultiShiftFilter();
  const { isSelfService } = useSelfService();

  const isRequestedOrBooked = (type === requested && isSelfService) || type === booked;

  const success = useAppSelector((state) =>
    successSelector([pharmacyActions.getPharmacyEventsByDate], state));

  const pIds = useCallback(() => {
    if (type === booked) {
      return events.map(
        (item) =>
item?.[idStore.id as keyof typeof item] as string,
      );
    }
    if (type === requested) {
      return events.flatMap((item) =>
        item.professionalIds ?? []) ?? [];
    }

    return [];
  }, [events, type]);

  const titleConfig = {
    [PharmacyLegendVariant.posted]: strings.postedWorkshiftFormat,
    [PharmacyLegendVariant.requested]: strings.requestedWorkshiftsFormat,
    [PharmacyLegendVariant.booked]: strings.bookedWorkshiftsFormat,
  };

  useEffect(() => {
    const title = (
      <DrawerOutOfDateTitle
        dateLabel={titleConfig?.[type ?? PharmacyLegendVariant.posted]}
        date={date}
      />
    );
    dispatch(setTitle({ title }));

    dispatch(setFooterShow(false));
    dispatch(
      getPharmacyEventsByDate({
        type,
        date: date.split('T')[0],
      }),
    );

    return () => {
      dispatch(resetStatus([pharmacyActions.getPharmacyEventsByDate]));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success && isRequestedOrBooked) {
      dispatch(getPharmacyProfessionalPictures(pIds()));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, pIds]);

  return (
    <>
      {events.map((item) =>
        (
          <StackedBox
            key={item.workshiftId}
            sx={styles.stackedContainer}
            showStacks={!!item.longTermShiftContractId}
            backgroundColor={
            stackedChipsColors[type as keyof typeof stackedChipsColors]
          }
          >
            <ServiceCard
              eventType={type}
              customContainerStyle={{ paddingBottom: 0, mb: 0 }}
              content={(
                <SummaryContent
                  event={item}
                  type={type}
                  preventInteraction={preventInteraction}
                />
            )}
            />
          </StackedBox>
        ))}
    </>
  );
};

export default WorkshiftSummaryList;
